import {useEffect, useState} from 'react';

import moment from 'moment';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Stack} from 'shells/layout/stack/stack';

import {ChatbotResponseTimingEnum, ContactFilter} from 'nutshell-graphql-types';

import {useGetChatSettings} from '../../inbox/chat/graphql/hooks/use-get-chat-settings';
import {PageContainer} from '../../layout';

import type {ReportKey, ReportURLParams} from '../report-types';

import {ChatbotReportContent} from './chatbot-report-content';
import {ChatbotReportControls} from './chatbot-report-controls';
import {ChatbotReportEmptyState, VARIANT} from './chatbot-report-empty-state';
import {ChatbotReportLoadingState} from './chatbot-report-loading-state';
import {DEFAULT_CHATBOT_REPORT_PERIOD} from './consts';
import {useGetChatbotReport} from './graphql/hooks/use-get-chatbot-report';

interface Props {
    reportKey: ReportKey;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => void;
    urlParams: ReportURLParams<any>;
}

export function ChatbotReportPage(props: Props) {
    const [selectedThreadType, setSelectedThreadType] = useState<ContactFilter>(ContactFilter.ALL);

    const {chatSettings, isLoading: isChatSettingsLoading} = useGetChatSettings();

    const isChatEnabled = chatSettings?.isChatEnabled ?? false;
    const isChatbotActive =
        chatSettings?.chatbot?.responseTiming !== ChatbotResponseTimingEnum.NEVER;
    const shouldShowOnboarding = !isChatEnabled || !isChatbotActive;

    const period =
        (safelyGetFilterByKey(props.activeFilters, 'period') as string) ||
        DEFAULT_CHATBOT_REPORT_PERIOD;

    // The default period `-d30` includes today, but the report should exclude it
    // (the date picker already enforces maxDate = yesterday). Replace the relative
    // period with an explicit range so the picker and query both reflect the right window.
    const isAdjustingPeriod = period === DEFAULT_CHATBOT_REPORT_PERIOD;

    useEffect(() => {
        if (isAdjustingPeriod) {
            const start = moment().subtract(30, 'days').startOf('day');
            const end = moment().subtract(1, 'day').endOf('day');
            props.onParamChange({filter: {period: `${start.unix()} TO ${end.unix()}`}}, true);
        }
    }, [isAdjustingPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

    const shouldSkipReport = isChatSettingsLoading || shouldShowOnboarding || isAdjustingPeriod;

    const {
        report,
        isLoading: isReportLoading,
        error,
    } = useGetChatbotReport(period, selectedThreadType, shouldSkipReport);

    if (isChatSettingsLoading) {
        return <ChatbotReportLoadingState />;
    }

    if (shouldShowOnboarding) {
        return <ChatbotReportEmptyState variant={VARIANT.Onboarding} />;
    }

    return (
        <PageContainer title='Chatbot report'>
            <Stack spacing={16}>
                <ChatbotReportControls
                    reportKey={props.reportKey}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    selectedThreadType={selectedThreadType}
                    setSelectedThreadType={setSelectedThreadType}
                />
                <ChatbotReportContent
                    chatSettings={chatSettings}
                    report={report}
                    isReportLoading={isReportLoading}
                    isAdjustingPeriod={isAdjustingPeriod}
                    period={period}
                    hasError={Boolean(error)}
                />
            </Stack>
        </PageContainer>
    );
}
